import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Switch,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons';
import lightappService from '../../services/lightappService';

const { Option } = Select;

const Registry = () => {
  const [loading, setLoading] = useState(false);
  const [registries, setRegistries] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRegistry, setEditingRegistry] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const [stats, setStats] = useState({ total: 0, defaults: 0 });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRegistries();
    fetchEndpoints();
  }, []);

  const fetchRegistries = async () => {
    setLoading(true);
    try {
      const response = await lightappService.getRegistries();
      if (response.success) {
        const data = response.data?.list || response.data || [];
        setRegistries(data);
        setStats({
          total: data.length,
          defaults: data.filter((r) => r.is_default).length,
        });
      } else {
        message.error(response.error || 'Failed to fetch registries');
      }
    } catch (error) {
      message.error('Failed to fetch registries');
    } finally {
      setLoading(false);
    }
  };

  const fetchEndpoints = async () => {
    try {
      const response = await lightappService.getEndpoints();
      if (response.success) {
        setEndpoints(response.data?.list || response.data || []);
      }
    } catch (error) {
      // non-critical
    }
  };

  const handleAdd = () => {
    setEditingRegistry(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRegistry(record);
    form.setFieldsValue({
      endpoint_id: record.endpoint_id,
      name: record.name,
      url: record.url,
      username: record.username,
      is_default: !!record.is_default,
    });
    // password intentionally left blank on edit
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await lightappService.deleteRegistry(id);
      if (response.success) {
        message.success('Registry deleted');
        fetchRegistries();
      } else {
        message.error(response.error || 'Failed to delete registry');
      }
    } catch (error) {
      message.error('Failed to delete registry');
    }
  };

  const handleSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      // On edit, omit password if blank
      const data = { ...values };
      if (editingRegistry && !data.password) {
        delete data.password;
      }

      let response;
      if (editingRegistry) {
        response = await lightappService.updateRegistry(editingRegistry.id, data);
      } else {
        response = await lightappService.createRegistry(data);
      }

      if (response.success) {
        message.success(`Registry ${editingRegistry ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        fetchRegistries();
      } else {
        message.error(response.error || 'Failed to save registry');
      }
    } catch (error) {
      message.error('Failed to save registry');
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <DatabaseOutlined />
          <span style={{ fontWeight: 500 }}>{text}</span>
          {record.is_default && <Tag color="gold" icon={<StarFilled />}>默认</Tag>}
        </Space>
      ),
    },
    {
      title: 'Registry URL',
      dataIndex: 'url',
      key: 'url',
      render: (url) => <code style={{ fontSize: 12 }}>{url}</code>,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (username) => username || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: '节点',
      dataIndex: 'endpoint_id',
      key: 'endpoint_id',
      render: (id) => {
        const ep = endpoints.find((e) => e.id === id);
        return ep ? <Tag>{ep.name}</Tag> : <Tag>Node-{id}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除该 Registry 吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="删除"
            cancelText="取消"
            okType="danger"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card>
            <Statistic title="Registry 总数" value={stats.total} />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title="默认 Registry"
              value={stats.defaults}
              valueStyle={{ color: '#d48806' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="镜像仓库管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加 Registry
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={registries}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无 Registry，点击右上角“添加 Registry”创建。"
              />
            ),
          }}
        />
      </Card>

      <Modal
        title={editingRegistry ? '编辑 Registry' : '添加 Registry'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={submitLoading}
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="endpoint_id"
            label="关联节点"
            rules={[{ required: true, message: '请选择关联节点' }]}
          >
            <Select placeholder="选择 Docker 节点">
              {endpoints.map((ep) => (
                <Option key={ep.id} value={ep.id}>
                  {ep.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="Registry 名称"
            rules={[{ required: true, message: '请输入 Registry 名称' }]}
          >
            <Input placeholder="如：My Private Registry" />
          </Form.Item>

          <Form.Item
            name="url"
            label="Registry URL"
            rules={[{ required: true, message: '请输入 Registry URL' }]}
          >
            <Input placeholder="如：registry.example.com 或 https://index.docker.io" />
          </Form.Item>

          <Form.Item name="username" label="用户名">
            <Input placeholder="Registry 用户名（可选）" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingRegistry ? '密码（留空则不修改）' : '密码'}
          >
            <Input.Password
              placeholder={editingRegistry ? '留空则不修改' : '输入密码'}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item name="is_default" label="设为默认" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Registry;
